import { useDraggable } from '@dnd-kit/core';
import { useLocalization } from '@lib/localization';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { reportDesignLimits, type ReportBlockType } from '@store/api/reportDesignEndpoints';
import { blockCatalog, definitionOf, type BlockDefinition } from '../blockCatalog';
import { groupTitleStyles } from '../designer.styles';
import {
  paletteEntryStyles, paletteGhostStyles, paletteGrabStyles, paletteGridStyles, paletteStyles,
} from './DesignCanvas.styles';
import { dragIds, splitListeners } from './dragging';

interface Props {
  /** Whether there are sheets to drag onto: the canvas is what is open. Without them an entry is only clicked. */
  draggable: boolean;
  /** The page a click would add to holds as many blocks as a page may. */
  full: boolean;
  /** Adds a block of a type to the page that is selected. */
  onAdd: (type: ReportBlockType) => void;
}

const EntryLook: React.FC<{ definition: BlockDefinition }> = ({ definition }) => {
  const { t } = useLocalization();
  const Icon = definition.icon;

  return (
    <>
      <Icon />
      <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{t(definition.label)}</Box>
    </>
  );
};

const PaletteEntry: React.FC<Props & { definition: BlockDefinition }> = ({ definition, draggable, full, onAdd }) => {
  const { t } = useLocalization();
  const { listeners, setNodeRef } = useDraggable({
    id: dragIds.palette(definition.type), data: { kind: 'palette', type: definition.type }, disabled: !draggable,
  });

  // The pointer's alone: from the keyboard an entry is a button, and Enter adds its block.
  const { pointer } = splitListeners(listeners);

  return (
    // The tooltip describes the entry and does not rename it: its name is the block's.
    <Tooltip describeChild title={full ? t('ReportDesign_Issue_Blocks', [reportDesignLimits.blocksPerPage]) : t(definition.description)}>
      <ButtonBase
        ref={setNodeRef}
        sx={draggable ? [paletteEntryStyles, paletteGrabStyles] : paletteEntryStyles}
        onClick={() => onAdd(definition.type)}
        {...pointer}
      >
        <EntryLook definition={definition} />
      </ButtonBase>
    </Tooltip>
  );
};

/** A palette entry as it looks while it is dragged: what the drag overlay shows under the pointer. */
export const PaletteGhost: React.FC<{ type: ReportBlockType }> = ({ type }) => {
  const definition = definitionOf(type);

  return definition ? <Box sx={paletteGhostStyles}><EntryLook definition={definition} /></Box> : null;
};

/**
 * What can be put on a cover or a closing page: the block catalog, an entry a type. An entry
 * is dragged onto a page, where it lands as a new block on the line the canvas shows, or
 * clicked, which adds the block at the end of the page that is selected. So a block can be
 * added without a drag, from the keyboard too.
 *
 * Only for a reader who may edit: the editor leaves the palette out for anybody else.
 */
const BlockPalette: React.FC<Props> = (props) => {
  const { t } = useLocalization();

  return (
    <Box sx={paletteStyles}>
      <Typography sx={{ ...groupTitleStyles, mt: 0 }}>{t('ReportDesign_Palette_Title')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.25, pb: 1 }}>
        {t('ReportDesign_Palette_Hint')}
      </Typography>
      <Box sx={paletteGridStyles}>
        {blockCatalog.map((definition) => <PaletteEntry key={definition.type} definition={definition} {...props} />)}
      </Box>
    </Box>
  );
};

export default BlockPalette;
